import {
  Fragment,
  ReactNode,
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useLayoutEffect,
} from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, EyeOff } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Card, CardContent, CardFooter } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import { Separator } from '../../../components/ui/separator';
import { CreateButton } from './CreateButton';
import { cn } from '../../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';
import {
  computeAutoHiddenColumns,
  computeColumnWidths,
  hasFloorOverflow,
  type ColumnLayoutSpec,
} from './dataTableLayout';
import {
  STICKY_ACTIONS_CELL,
  STICKY_CELL_SURFACE,
  STICKY_LEAD_CELL,
  STICKY_LEAD_CELL_OFFSET,
  stickyIdentityOffset,
} from './stickyColumns';

/**
 * Column priority tiers for overflow-aware auto-hide.
 *
 * 1 — Essential (entity identity, e.g. Name, Subject, ID). Never auto-dropped.
 * 2 — Important (drives next action: Status, Assignee, Priority, key dates). Dropped last.
 * 3 — Useful (secondary metadata: Contact, Modified, Category).
 * 4 — Optional (power-user fields: Created, internal IDs, system flags).
 *
 * When the table can't fit all columns in its container, columns are dropped in
 * descending priority order until it fits. See docs/design-system/ui-list-pages.md §4.1.
 */
export type ColumnPriority = 1 | 2 | 3 | 4;

export interface Column<T> {
  id: keyof T | string;
  label: string;
  /** Optional compact header content (e.g. an icon) shown in the desktop table
   *  header instead of `label`. `label` is still used by the column selector and
   *  the mobile card view, so it must stay a readable string. */
  headerLabel?: ReactNode;
  /** Preferred pixel width. Mutually exclusive with `flex`. Without `minWidth`
   *  this is a hard width the column never shrinks below. */
  width?: number | string;
  /** Flex weight for distributing remaining container width. Mutually exclusive with `width`. */
  flex?: number;
  /** The narrowest the column may render. Pair with `width` to make a fixed column
   *  shrinkable (renders at `width`, squeezed toward `minWidth` before any sibling
   *  is auto-hidden); pair with `flex` to floor a flexible column.
   *  See `dataTableLayout.ts` for the full width contract. */
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  resizable?: boolean;
  render?: (row: T) => ReactNode;
  /** Hide this column in mobile card view */
  hideOnMobile?: boolean;
  /**
   * On the mobile card, render this cell UNLABELLED beside the primary value
   * instead of as one of the card's labelled secondary rows.
   *
   * The card shows at most `MAX_MOBILE_SECONDARY` secondaries, so an
   * always-present qualifier of the row's identity — the owning company under
   * the "All companies" scope, where two entities each hold a `PO-00001` — can
   * only earn a slot by evicting Status or Amount. `hideOnMobile` is the other
   * half of that trap: it makes the qualifier vanish at exactly the width where
   * rows are densest and the ambiguity is worst. This is the third option —
   * costs no slot, displaces nothing.
   */
  mobileInline?: boolean;
  /** Function to calculate the total for this column. If provided, column will show a total in the totals row. */
  total?: (data: T[]) => ReactNode;
  /** Additional CSS class for each cell in this column (header + body) */
  cellClassName?: string;
  /** Priority tier (1-4) for overflow-aware auto-hide. See ColumnPriority. */
  priority?: ColumnPriority;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  isLoading?: boolean;
  emptyMessage?: string;
  /**
   * Optional create CTA shown beneath the empty message when the table has no
   * rows. Renders the shared `CreateButton` (Plus + entity name) so an empty
   * DataTable matches the create affordance used in the page header. Pass the
   * entity name only as `label` (e.g. `t('common:entities.user')`) — no verbs.
   */
  emptyAction?: { label: string; onClick: () => void };
  // Sorting
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  // Pagination
  /** Current page, 1-based (first page is 1) — see docs/design-system/ui-list-pages.md §11 */
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  /** Callback when page size changes. When provided, shows a page size selector. */
  onPageSizeChange?: (pageSize: number) => void;
  /** Options for the page size selector */
  pageSizeOptions?: number[];
  /**
   * Extra classes per row, for row STATE the table itself does not own —
   * keyboard focus in a picker, a stale/pending optimistic row, a diff
   * highlight. Applied to the desktop `<TableRow>` and the mobile `<Card>`
   * alike, so a caller cannot style one viewport and silently miss the other.
   *
   * NOT for selection (`selectable` + `selectedIds` already render that, and a
   * second source of truth for the same state is how the two drift apart) and
   * not for per-column styling (`Column.cellClassName`).
   */
  rowClassName?: (row: T) => string | undefined;
  // Row click handler
  onRowClick?: (row: T) => void;
  /**
   * Makes each row a real navigable link. When provided, the row's **primary
   * cell** (the first visible column — the entity identity by convention) is
   * rendered as a real `<Link>` to `rowHref(row)`, and the whole row navigates
   * on a plain left-click as a convenience. Because the primary cell is a true
   * anchor, cmd/ctrl-click and middle-click "open in new tab" work, and the URL
   * previews on hover — none of which a bare `onRowClick` gives.
   *
   * Return `null` for a non-navigable row. Build the href with `getEntityUrl()`
   * (prefix-aware via `useEmbeddingPrefix()` on embedded cross-module routes).
   *
   * Interactive cells (checkbox, actions) already stop propagation, and the
   * row-click guard ignores clicks that land on any nested anchor or carry a
   * modifier key, so those keep working. Prefer this over `onRowClick` for
   * navigation; use `onRowClick` only for non-navigation row behavior.
   *
   * The anchor wraps the identity column — by default the first declared column,
   * overridable with `rowLinkColumnId`. When a table also supplies
   * `renderRowWrapper` (e.g. DnD rows), that wrapper owns the whole-row markup and
   * bypasses the built-in click guard; the primary-cell anchor still renders, but
   * the wrapper must guard its own row click with the exported `shouldNavigateRow`.
   * See docs/design-system/ui-list-pages.md §4.
   */
  rowHref?: (row: T) => string | null;
  /**
   * Which column `rowHref` turns into the real anchor (the entity identity).
   * Defaults to the first declared column. Set this when column 0 is not the
   * identity cell (e.g. a leading severity/status badge) — the analogue of
   * `mobilePrimaryField` for the desktop row link. The referenced column's
   * content must be non-interactive (no nested anchor/button).
   */
  rowLinkColumnId?: string;
  // Row actions column
  actionsColumn?: (row: T) => ReactNode;
  /** Width of the actions column (default: 120px) */
  actionsColumnWidth?: number;
  // Column resizing
  resizableColumns?: boolean;
  onColumnResize?: (columnId: string, width: number) => void;
  // Mobile view options
  /** 'cards' = always cards, 'scroll' = always table with scroll, 'auto' = cards on mobile */
  mobileView?: 'cards' | 'scroll' | 'auto';
  /** Field to show as card title (defaults to first column) */
  mobilePrimaryField?: keyof T;
  /** Custom card renderer for complete control */
  mobileCardRenderer?: (row: T, actions?: ReactNode) => ReactNode;
  /** Show totals row at bottom of table (uses column.total functions) */
  showTotals?: boolean;
  /** Label for the totals row (defaults to "Total") */
  totalsLabel?: string;
  className?: string;
  // Row selection
  /** Enable row selection checkboxes */
  selectable?: boolean;
  /** Array of selected row key values */
  selectedIds?: Array<string | number>;
  /** Callback when selection changes */
  onSelectionChange?: (selectedIds: Array<string | number>) => void;
  /**
   * Per-row gate on selection. A row that returns `false` keeps its place in the
   * table — with its own explanation of why it can't be picked — but its checkbox
   * is disabled and select-all skips it. Show-and-disable, not filter-out: a row
   * that silently disappears leaves the user with no way to learn where it went.
   * Defaults to every row being selectable.
   */
  isRowSelectable?: (row: T) => boolean;
  // Sticky columns for horizontal scroll — both default to ON, see ./stickyColumns.ts
  /**
   * Pin the lead columns (selection checkbox + identity cell) to the inline start
   * while the table scrolls sideways, so a scrolled-right row still says which
   * record it is. **Desktop only** — at 375px two pinned edges would leave almost
   * nothing scrollable between them. Defaults to `true`; pass `false` for a table
   * whose first column is not an identity (a leading action or spacer column).
   * Automatically off when `prependColumnHeader` is set — see `pinLeadColumns`.
   */
  stickyFirstColumn?: boolean;
  /**
   * Pin the row-actions column to the inline end at every viewport, so the
   * three-dot menu is always reachable without scrolling to it. Defaults to `true`;
   * pass `false` only for a table whose trailing column must scroll away.
   */
  stickyActionsColumn?: boolean;
  // DnD / custom row wrapper
  /** Prepend an extra column header (e.g. for a drag handle) */
  prependColumnHeader?: ReactNode;
  /** Wrap each table row with a custom component (e.g. for DnD sortable rows).
   *  Receives the row data and the rendered TableCell nodes as children.
   *  Put `group/row` on the row you render if you want the pinned lead/actions
   *  cells to track its hover and selected tint (see ./stickyColumns.ts). */
  renderRowWrapper?: (row: T, cells: ReactNode, rowKey: string) => ReactNode;
  /**
   * Per-row expandable DETAIL — the block that is too big for a cell
   * (`docs/design-system/ui-list-pages.md` § 5: "an expandable row, not a second
   * list"). Return the panel for a row the caller considers open, and
   * `null`/`undefined` for every other row.
   *
   * The caller keeps the open/closed state and renders the toggle itself, in one
   * of its own `Column.render`s — so the disclosure affordance sits where the
   * reader looks for it, carries its own `aria-expanded`, and this table stays
   * ignorant of what "expanded" means. All this prop owns is WHERE the returned
   * node goes.
   *
   * It goes in BOTH viewports, which is the whole reason it exists: the
   * `renderRowWrapper` recipe several list pages use for expansion
   * (`ContainerLoadsListPage`, `ShipmentBookingsTab`) only reaches the desktop
   * `<table>`, so at 375px — where `DataTable` renders cards — those rows simply
   * do not expand. That is survivable where the detail is a convenience and fatal
   * where the detail IS the feature.
   *
   * Composes with `renderRowWrapper`: the wrapper still owns the row's own
   * markup, and the detail row is appended after whatever it returns.
   */
  renderRowDetail?: (row: T) => ReactNode;
  // Column priority / auto-hide
  /** Column IDs the user has explicitly hidden (e.g. via ColumnSelector). Overrides auto-hide. */
  userHiddenColumns?: string[] | Set<string>;
  /** Fired with the set of column IDs auto-hidden due to overflow (excluding user-hidden). */
  onAutoHiddenChange?: (autoHiddenIds: string[]) => void;
  /** Fired when the user clicks the built-in "+N hidden" chip — typically opens ColumnSelector. */
  onAutoHiddenChipClick?: () => void;
}

/** Project a `Column<T>` onto the geometry-only shape the layout maths needs. */
function toLayoutSpec<T>(c: Column<T>): ColumnLayoutSpec {
  return {
    id: String(c.id),
    width: c.width,
    minWidth: c.minWidth,
    flex: c.flex,
    priority: c.priority,
  };
}

/**
 * Whether a whole-row click should fall through to SPA navigation (the `rowHref`
 * convenience). Real links (the primary-cell anchor, in-row links), other
 * interactive controls, modifier-key clicks, and already-handled events keep
 * their native behavior — so cmd/ctrl-click and middle-click "open in new tab",
 * checkboxes, and action menus are never hijacked by the convenience click.
 *
 * Exported so tables that supply their own row markup via `renderRowWrapper`
 * (e.g. DnD-sortable rows) can guard their whole-row click identically instead
 * of re-deriving — and re-inlining the selector literal — per call site.
 */
export function shouldNavigateRow(e: React.MouseEvent): boolean {
  if (e.defaultPrevented || e.button !== 0) return false;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  const target = e.target as HTMLElement;
  return !target.closest('a, button, input, select, textarea, label, [role="menuitem"], [role="button"]');
}

/** The primary (identity) cell rendered as a real anchor. Inherits the cell's
 *  text color — the only visual change is the standard hover-underline + focus
 *  ring affordance, so a row's identity looks unchanged until interacted with. */
function PrimaryCellLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Link
      to={href}
      className="rounded-sm outline-none hover:underline focus-visible:underline focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </Link>
  );
}

/** A navigable table row. Isolated into its own component so `useNavigate()` is
 *  only called when `rowHref` is in use — DataTable renders outside a Router in
 *  some unit tests, and the non-navigable path must not require router context. */
function NavigableRow({
  href,
  isSelected,
  children,
}: {
  href: string | null;
  isSelected: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <TableRow
      data-testid="datatable-row"
      onClick={href ? (e) => { if (shouldNavigateRow(e)) navigate(href); } : undefined}
      // `group/row`: a pinned (sticky) cell needs an opaque backdrop, which would
      // otherwise make it the one column that never picks up the row's hover or
      // selected tint. See ./stickyColumns.ts.
      className={cn('group/row', href && 'cursor-pointer', isSelected && 'bg-muted/50')}
      data-state={isSelected ? 'selected' : undefined}
    >
      {children}
    </TableRow>
  );
}

/** Mobile-card equivalent of `NavigableRow` — same router-isolation rationale. */
function NavigableCard({
  href,
  isSelected,
  children,
}: {
  href: string | null;
  isSelected: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <Card
      className={cn(href && 'cursor-pointer', isSelected && 'ring-2 ring-primary')}
      onClick={href ? (e) => { if (shouldNavigateRow(e)) navigate(href); } : undefined}
    >
      {children}
    </Card>
  );
}

export function DataTable<T>({
  columns,
  data,
  keyField,
  isLoading = false,
  emptyMessage,
  emptyAction,
  sortBy,
  sortDirection = 'asc',
  onSort,
  page = 1,
  pageSize = 20,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  rowClassName,
  onRowClick,
  rowHref,
  rowLinkColumnId,
  actionsColumn,
  actionsColumnWidth = 120,
  resizableColumns = false,
  onColumnResize,
  mobileView = 'auto',
  mobilePrimaryField,
  mobileCardRenderer,
  showTotals = false,
  totalsLabel,
  className,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  isRowSelectable,
  stickyFirstColumn = true,
  stickyActionsColumn = true,
  prependColumnHeader,
  renderRowWrapper,
  renderRowDetail,
  userHiddenColumns,
  onAutoHiddenChange,
  onAutoHiddenChipClick,
}: DataTableProps<T>) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const resolvedEmptyMessage = emptyMessage ?? t('app.noData');
  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 0;
  const showPagination = totalPages > 1 && onPageChange;
  const showCardView = mobileView === 'cards' || (mobileView === 'auto' && isMobile);

  // Keep controlled consumers inside the 1-based range when filtering, deleting,
  // or refetching reduces the number of pages. Without this, a client-paginated
  // table can render an empty slice and hide the only navigation back to its data.
  //
  // Never clamp while loading: a server-paginated consumer keys its query by page,
  // so changing page yields `data === undefined` and a transient `totalCount` of 0
  // until the fetch lands. Clamping on that would snap the user straight back to
  // page 1 and make every page but the first unreachable.
  useEffect(() => {
    if (!onPageChange || totalCount === undefined || isLoading) return;
    const validPage = Math.min(Math.max(page, 1), Math.max(totalPages, 1));
    if (validPage !== page) onPageChange(validPage);
  }, [isLoading, onPageChange, page, totalCount, totalPages]);

  // User-resized column widths ONLY — a drag on the resize handle records the new
  // width here and it wins over everything else. Deliberately NOT seeded from
  // `col.width`: a seeded entry would shadow `computedWidths` for every
  // fixed-width column, silently discarding the priority system's shrink and
  // pushing the table into horizontal scroll.
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  // Resize handling
  const resizeRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnId: string, currentWidth: number) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        columnId,
        startX: e.clientX,
        startWidth: currentWidth,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!resizeRef.current) return;
        const diff = moveEvent.clientX - resizeRef.current.startX;
        const col = columns.find((c) => String(c.id) === resizeRef.current!.columnId);
        const minWidth = col?.minWidth || 50;
        const newWidth = Math.max(minWidth, resizeRef.current.startWidth + diff);
        setColumnWidths((prev) => ({
          ...prev,
          [resizeRef.current!.columnId]: newWidth,
        }));
      };

      const handleMouseUp = () => {
        if (resizeRef.current && onColumnResize) {
          const finalWidth = columnWidths[resizeRef.current.columnId];
          if (finalWidth) {
            onColumnResize(resizeRef.current.columnId, finalWidth);
          }
        }
        resizeRef.current = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [columns, columnWidths, onColumnResize]
  );

  const handleSort = (field: string) => {
    if (onSort) {
      onSort(field);
    }
  };

  // ----- Column priority / auto-hide system -----
  // Engaged when ANY column declares `priority` or `flex` (opt-in for backward compatibility).
  const usesPrioritySystem = useMemo(
    () => columns.some((c) => c.priority !== undefined || c.flex !== undefined),
    [columns]
  );

  const userHiddenSet = useMemo(() => {
    if (!userHiddenColumns) return new Set<string>();
    return userHiddenColumns instanceof Set ? userHiddenColumns : new Set(userHiddenColumns);
  }, [userHiddenColumns]);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [autoHiddenIds, setAutoHiddenIds] = useState<string[]>([]);
  /**
   * The visible width of the scroll port, measured only when a row detail can
   * render — it is what stops the expansion panel from scrolling away sideways.
   *
   * A `colSpan` cell is as wide as the TABLE, and a table wider than its
   * container scrolls: so a panel laid out at the cell's own width starts at the
   * table's left edge, which on a scrolled table is off-screen, and the reader
   * sees the middle of the panel with its first tile cut off. Pinning the panel
   * `sticky start-0` at exactly this width keeps it filling the visible band at
   * every scroll offset. 0 (jsdom, first paint) falls back to the cell's own
   * width, which is the un-pinned behaviour.
   */
  const [scrollportWidth, setScrollportWidth] = useState<number>(0);

  // ResizeObserver on the table container (only when priority system is engaged).
  useLayoutEffect(() => {
    if (!usesPrioritySystem) return;
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [usesPrioritySystem]);

  useLayoutEffect(() => {
    if (!renderRowDetail) return;
    const el = tableContainerRef.current;
    if (!el) return;
    const update = () => setScrollportWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [renderRowDetail]);

  // Width available to the data columns, once the checkbox + actions gutters are reserved.
  const availableWidth = useMemo(() => {
    const reserved = (selectable ? 40 : 0) + (actionsColumn ? actionsColumnWidth : 0);
    return Math.max(0, containerWidth - reserved);
  }, [containerWidth, selectable, actionsColumn, actionsColumnWidth]);

  // Compute which columns to auto-hide based on container width and priorities.
  // Columns are compared at their *floors*, so a shrinkable column (width +
  // minWidth) is squeezed rather than dropped. See dataTableLayout.ts.
  const computedAutoHidden = useMemo<string[]>(() => {
    if (!usesPrioritySystem || containerWidth <= 0 || showCardView) return [];
    // User-hidden columns are out of contention entirely.
    const candidates = columns
      .filter((c) => !userHiddenSet.has(String(c.id)))
      .map(toLayoutSpec);
    return computeAutoHiddenColumns(candidates, availableWidth);
  }, [usesPrioritySystem, containerWidth, showCardView, availableWidth, columns, userHiddenSet]);

  // Notify parent when auto-hidden set changes. We keep `onAutoHiddenChange` in a ref so an
  // unstable inline callback from the parent doesn't retrigger this effect (rerender loop).
  const onAutoHiddenChangeRef = useRef(onAutoHiddenChange);
  useEffect(() => {
    onAutoHiddenChangeRef.current = onAutoHiddenChange;
  }, [onAutoHiddenChange]);

  useEffect(() => {
    if (autoHiddenIds.length === computedAutoHidden.length &&
        autoHiddenIds.every((id, i) => id === computedAutoHidden[i])) {
      return;
    }
    setAutoHiddenIds(computedAutoHidden);
    onAutoHiddenChangeRef.current?.(computedAutoHidden);
  }, [computedAutoHidden, autoHiddenIds]);

  const hiddenColumnIds = useMemo(() => {
    const set = new Set<string>(autoHiddenIds);
    userHiddenSet.forEach((id) => set.add(id));
    return set;
  }, [autoHiddenIds, userHiddenSet]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => !hiddenColumnIds.has(String(c.id))),
    [columns, hiddenColumnIds]
  );

  // Which pins are actually in force this render (policy in ./stickyColumns.ts).
  //
  // The lead pin is desktop-only by design; the actions pin is not, because the
  // card view is what answers mobile for most tables and a table that opted out of
  // it (`mobileView="scroll"`) is exactly the one whose menu scrolls off screen.
  //
  // A table can also declare its actions inside `columns` as a trailing column with
  // `id: 'actions'` — 35 files do — rather than through the `actionsColumn` prop.
  // Pin that shape too, or a third of the tree keeps the defect the default fixes.
  // `!isMobile`, not `!showCardView`: a table that opted out of the card view with
  // `mobileView="scroll"` is still on a phone, where two pinned edges would leave
  // almost nothing scrollable between them.
  //
  // `!prependColumnHeader`: that prop puts a caller-owned cell (a drag handle) BEFORE
  // everything DataTable knows about, at a width DataTable cannot see — so the offset
  // maths has nothing to add and the pinned identity cell would come to rest ON TOP of
  // the un-pinned handle as it scrolls under. The lead pin is off for those tables;
  // the actions pin, which measures from the other edge, is unaffected.
  const pinLeadColumns = stickyFirstColumn && !isMobile && !prependColumnHeader;
  // Only when the prop is absent: a table using both would otherwise pin two cells
  // to the same edge, stacking one over the other.
  const trailingActionsColumnId = useMemo(() => {
    if (actionsColumn) return undefined;
    const last = visibleColumns[visibleColumns.length - 1];
    return last && String(last.id) === 'actions' ? 'actions' : undefined;
  }, [actionsColumn, visibleColumns]);
  const pinActionsColumn = stickyActionsColumn && (Boolean(actionsColumn) || Boolean(trailingActionsColumnId));
  // The cell that comes to rest at the inline start. With a selection checkbox
  // there are two pinned lead cells; without one, the identity cell is flush.
  const identityColumnId = visibleColumns[0] ? String(visibleColumns[0].id) : undefined;

  // The identity column that `rowHref` turns into a real anchor: `rowLinkColumnId`
  // if given, else the declared first column. Compared by id (not position) so a
  // hidden identity column never causes a shifted-in, possibly-interactive column
  // to be link-wrapped.
  const primaryLinkColumnId =
    rowLinkColumnId ?? (columns[0] ? String(columns[0].id) : undefined);

  // Compute final per-column widths for fixed layout (only when priority system engaged).
  const computedWidths = useMemo<Record<string, number | string | undefined>>(() => {
    if (!usesPrioritySystem) return {};
    return computeColumnWidths(visibleColumns.map(toLayoutSpec), availableWidth);
  }, [usesPrioritySystem, visibleColumns, availableWidth]);

  // Even the floors overflow (only priority-1 columns left) → horizontal scroll
  // with a right-edge fade. Preferred widths overflowing is not enough: the
  // shrinkable columns absorb that.
  const priority1Overflow = useMemo(() => {
    if (!usesPrioritySystem || containerWidth <= 0 || showCardView) return false;
    return hasFloorOverflow(visibleColumns.map(toLayoutSpec), availableWidth);
  }, [usesPrioritySystem, containerWidth, showCardView, visibleColumns, availableWidth]);
  // ----- end column priority / auto-hide system -----

  const getColumnWidth = (col: Column<T>): number | string | undefined => {
    const colId = String(col.id);
    if (columnWidths[colId]) {
      return columnWidths[colId];
    }
    if (usesPrioritySystem && computedWidths[colId] !== undefined) {
      return computedWidths[colId];
    }
    return col.width;
  };

  // Selection helpers
  const dataIds = useMemo(() =>
    data.map((row) => row[keyField] as string | number),
    [data, keyField]
  );

  // Select-all acts on the rows that CAN be selected, so a disabled row never
  // sneaks into the selection and never holds the header checkbox back from
  // reading "all selected".
  const selectableIds = useMemo(
    () =>
      isRowSelectable
        ? data.filter((row) => isRowSelectable(row)).map((row) => row[keyField] as string | number)
        : dataIds,
    [data, dataIds, isRowSelectable, keyField]
  );

  // Disabled ONLY when there are rows and the gate rejects every one of them.
  // An EMPTY table keeps its previous (enabled, inert) header — every selectable
  // table in the app renders one, and disabling those would be an unrelated
  // visual change nobody asked for.
  const selectAllDisabled = data.length > 0 && selectableIds.length === 0;

  const isAllSelected = useMemo(() =>
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id)),
    [selectableIds, selectedIds]
  );

  const isPartiallySelected = useMemo(() =>
    !isAllSelected && selectableIds.some((id) => selectedIds.includes(id)),
    [isAllSelected, selectableIds, selectedIds]
  );

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (isAllSelected) {
      // Deselect all visible rows
      onSelectionChange(selectedIds.filter((id) => !selectableIds.includes(id)));
    } else {
      // Select all visible SELECTABLE rows (merge with existing selection)
      const newSelection = [...selectedIds];
      selectableIds.forEach((id) => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      onSelectionChange(newSelection);
    }
  }, [onSelectionChange, isAllSelected, selectedIds, selectableIds]);

  const handleSelectRow = useCallback((rowId: string | number, selectable = true) => {
    if (!onSelectionChange || !selectable) return;
    if (selectedIds.includes(rowId)) {
      onSelectionChange(selectedIds.filter((id) => id !== rowId));
    } else {
      onSelectionChange([...selectedIds, rowId]);
    }
  }, [onSelectionChange, selectedIds]);

  const renderLoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          {selectable && (
            <TableCell className={cn('w-[40px]', pinLeadColumns && [STICKY_LEAD_CELL, 'bg-background'])}>
              <Skeleton className="h-4 w-4" />
            </TableCell>
          )}
          {visibleColumns.map((col) => (
            <TableCell
              key={String(col.id)}
              style={
                pinLeadColumns && String(col.id) === identityColumnId
                  ? stickyIdentityOffset(selectable)
                  : undefined
              }
              className={cn(
                pinLeadColumns &&
                  String(col.id) === identityColumnId && [STICKY_LEAD_CELL_OFFSET, 'bg-background'],
                pinActionsColumn &&
                  String(col.id) === trailingActionsColumnId && [STICKY_ACTIONS_CELL, 'bg-background']
              )}
            >
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
          {actionsColumn && (
            <TableCell className={cn(pinActionsColumn && [STICKY_ACTIONS_CELL, 'bg-background'])}>
              <Skeleton className="h-4 w-20" />
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  );

  const renderMobileLoadingSkeleton = () => (
    <div className="space-y-4">
      {[...Array(3)].map((_, index) => (
        <Card key={index}>
          <CardContent className="pb-2 pt-4">
            <Skeleton className="h-6 w-3/5 mb-2" />
            <Skeleton className="h-4 w-4/5 mb-1" />
            <Skeleton className="h-4 w-2/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderEmptyState = () => (
    <TableRow>
      <TableCell
        colSpan={visibleColumns.length + (selectable ? 1 : 0) + (actionsColumn ? 1 : 0)}
        className="py-8 text-center text-muted-foreground"
      >
        {emptyAction ? (
          <div className="flex flex-col items-center gap-3">
            <span>{resolvedEmptyMessage}</span>
            <CreateButton onClick={emptyAction.onClick}>{emptyAction.label}</CreateButton>
          </div>
        ) : (
          resolvedEmptyMessage
        )}
      </TableCell>
    </TableRow>
  );

  const renderMobileEmptyState = () => (
    <Card className="p-8 text-center">
      {emptyAction ? (
        <div className="flex flex-col items-center gap-3">
          <p className="text-muted-foreground">{resolvedEmptyMessage}</p>
          <CreateButton onClick={emptyAction.onClick}>{emptyAction.label}</CreateButton>
        </div>
      ) : (
        <p className="text-muted-foreground">{resolvedEmptyMessage}</p>
      )}
    </Card>
  );

  // Mobile card view
  const renderMobileCardView = () => {
    if (isLoading) return renderMobileLoadingSkeleton();
    if (data.length === 0) return renderMobileEmptyState();

    const primaryField = mobilePrimaryField || (columns[0]?.id as keyof T);
    // Start from `visibleColumns` — the same set the desktop table renders — so a
    // column the user hides via ColumnSelector is dropped from the card too.
    // (Overflow auto-hide is desktop-only: `autoHiddenIds` is empty in card mode,
    // so `visibleColumns` here differs from `columns` only by the user's choices.)
    const mobileColumns = visibleColumns.filter(
      (col) => col.id !== primaryField && !col.hideOnMobile && !col.mobileInline
    );
    // Rendered under the primary value, outside the secondary budget above.
    const inlineColumns = visibleColumns.filter(
      (col) => col.id !== primaryField && !col.hideOnMobile && col.mobileInline
    );
    // A card only has room for a few secondary rows, so pick the MOST IMPORTANT
    // ones by `priority` (1 = essential … 4 = optional) rather than blindly taking
    // the first-declared. Otherwise a low-priority-but-early column (e.g. a verbose
    // Description) crowds out a priority-2 field the user needs (e.g. Status). The
    // chosen columns are then rendered back in declaration order so the card layout
    // matches the table and lists with ≤N mobile columns are unaffected. An
    // undeclared priority defaults to 1 (essential) — matching the desktop auto-hide
    // convention in dataTableLayout.ts, so a no-priority column is never dropped
    // from the card while a declared priority-2 sibling stays.
    const MAX_MOBILE_SECONDARY = 4;
    const secondaryColumns = mobileColumns
      .map((col, index) => ({ col, index }))
      .sort(
        (a, b) => (a.col.priority ?? 1) - (b.col.priority ?? 1) || a.index - b.index
      )
      .slice(0, MAX_MOBILE_SECONDARY)
      .sort((a, b) => a.index - b.index)
      .map((entry) => entry.col);

    return (
      <div className="space-y-3">
        {selectable && data.length > 0 && (
          <div className="flex items-center gap-2 px-1">
            <div
              className={cn(
                'flex items-center',
                selectAllDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
              onClick={handleSelectAll}
            >
              <Checkbox
                checked={isPartiallySelected ? 'indeterminate' : isAllSelected}
                disabled={selectAllDisabled}
                aria-label={t('app.selectAll')}
              />
            </div>
            <span className="text-sm text-muted-foreground">
              {t('app.selectAll')}
            </span>
          </div>
        )}
        {data.map((row) => {
          const rowId = row[keyField] as string | number;
          const isRowSelected = selectedIds.includes(rowId);
          const rowSelectable = isRowSelectable ? isRowSelectable(row) : true;
          const actions = actionsColumn ? actionsColumn(row) : undefined;

          if (mobileCardRenderer) {
            return (
              <div key={String(rowId)}>{mobileCardRenderer(row, actions)}</div>
            );
          }

          const cardDetail = renderRowDetail ? renderRowDetail(row) : null;
          const primaryColumn = columns.find((col) => col.id === primaryField);
          const primaryValue = primaryColumn?.render
            ? primaryColumn.render(row)
            : String(row[primaryField] ?? '');
          const cardHref = rowHref ? rowHref(row) : null;
          const primaryNode = cardHref ? (
            <PrimaryCellLink href={cardHref}>{primaryValue}</PrimaryCellLink>
          ) : (
            primaryValue
          );

          const cardBody = (
            <>
              <CardContent className={cn('pt-4', actionsColumn || selectable ? 'pb-0' : 'pb-4')}>
                {selectable && (
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className={cn(
                        'flex items-center',
                        rowSelectable ? 'cursor-pointer' : 'cursor-not-allowed'
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectRow(rowId, rowSelectable);
                      }}
                    >
                      <Checkbox
                        checked={isRowSelected}
                        disabled={!rowSelectable}
                        aria-label={t('app.selectRow')}
                      />
                    </div>
                    <div className="font-medium flex-1">{primaryNode}</div>
                  </div>
                )}
                {!selectable && <div className="font-medium mb-2">{primaryNode}</div>}
                {inlineColumns.length > 0 && (
                  <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2">
                    {inlineColumns.map((col) => (
                      <span key={String(col.id)} className="min-w-0">
                        {col.render ? col.render(row) : String(row[col.id as keyof T] ?? '')}
                      </span>
                    ))}
                  </div>
                )}
                {secondaryColumns.map((col) => (
                  <div
                    key={String(col.id)}
                    className="flex justify-between items-start py-1"
                  >
                    {/* No `dir`: the mobile card's field label is the same
                        translated column label as the desktop header. */}
                    <span className="text-sm text-muted-foreground shrink-0 me-4">
                      {col.label}:
                    </span>
                    <span className="min-w-0 text-sm text-end break-words">
                      {col.render ? col.render(row) : String(row[col.id as keyof T] ?? '')}
                    </span>
                  </div>
                ))}
                {/* The card's half of `renderRowDetail`. Full card width rather
                    than the label/value split above: the detail is a PANEL, and
                    ~180px of right-hand column is exactly the constraint the
                    desktop expansion row exists to escape. */}
                {cardDetail && (
                  <div
                    data-testid="datatable-row-detail"
                    className="-mx-2 mt-2 rounded-md bg-muted/30 px-2 py-2"
                  >
                    {cardDetail}
                  </div>
                )}
              </CardContent>
              {(actionsColumn || (selectable && !actionsColumn)) && (
                <>
                  <Separator />
                  <CardFooter className="justify-end py-2">
                    {actions}
                  </CardFooter>
                </>
              )}
            </>
          );

          if (rowHref) {
            return (
              <NavigableCard key={String(rowId)} href={cardHref} isSelected={isRowSelected}>
                {cardBody}
              </NavigableCard>
            );
          }

          return (
            <Card
              key={String(rowId)}
              className={cn(
                onRowClick && 'cursor-pointer',
                isRowSelected && 'ring-2 ring-primary',
                rowClassName?.(row)
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {cardBody}
            </Card>
          );
        })}
      </div>
    );
  };

  // Sticky cells need the DataTable's own container to be the scroll port, so the
  // shadcn `Table` must not wrap the table in a second `overflow-auto` div.
  const hasStickyColumns = pinLeadColumns || pinActionsColumn;

  // Table view (desktop or when mobileView='scroll')
  const renderTableView = () => (
    <div className="relative">
      {usesPrioritySystem && !showCardView && autoHiddenIds.length > 0 && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={onAutoHiddenChipClick}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border border-dashed px-2.5 py-1 text-xs text-muted-foreground bg-muted/40',
              // Keyboard users need to see this chip take focus — it is the only
              // route to the hidden columns when the table overflows.
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              onAutoHiddenChipClick
                ? 'hover:bg-muted hover:text-foreground cursor-pointer touch:min-h-[44px]'
                : 'cursor-default',
            )}
            title={t('app.autoHiddenColumnsHint', {
              defaultValue: 'Hidden because of insufficient width',
            })}
            aria-label={t('app.autoHiddenColumnsAria', {
              defaultValue: '{{count}} columns auto-hidden',
              count: autoHiddenIds.length,
            })}
          >
            <EyeOff className="h-3 w-3" />
            {t('app.autoHiddenChip', {
              defaultValue: '+{{count}} hidden',
              count: autoHiddenIds.length,
            })}
          </button>
        </div>
      )}
      <div ref={tableContainerRef} className="rounded-md border overflow-auto relative">
      {/* The right-edge fade hints "there is more to scroll". Suppress it when the
          actions column is pinned there: at z-20 it would paint over the pinned
          menu, and the pinned cell's own separating shadow already marks the edge. */}
      {priority1Overflow && !pinActionsColumn && (
        <div
          className="pointer-events-none absolute top-0 right-0 bottom-0 w-8 z-20 bg-gradient-to-l from-background to-transparent"
          aria-hidden="true"
        />
      )}
      <Table
        style={{ tableLayout: usesPrioritySystem || resizableColumns ? 'fixed' : 'auto' }}
        noWrapper={hasStickyColumns}
      >
        <TableHeader>
          <TableRow>
            {prependColumnHeader}
            {selectable && (
              <TableHead
                className={cn(
                  'w-[40px]',
                  pinLeadColumns && [STICKY_LEAD_CELL, 'bg-background']
                )}
              >
                <div
                  className={cn(
                    'flex items-center justify-center',
                    selectAllDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  )}
                  onClick={handleSelectAll}
                >
                  <Checkbox
                    checked={isPartiallySelected ? 'indeterminate' : isAllSelected}
                    disabled={selectAllDisabled}
                    aria-label={t('app.selectAll')}
                  />
                </div>
              </TableHead>
            )}
            {visibleColumns.map((col) => {
              const colId = String(col.id);
              const width = getColumnWidth(col);
              const isResizable = resizableColumns && col.resizable !== false;
              const isSorted = sortBy === col.id;

              const isPinnedIdentity = pinLeadColumns && colId === identityColumnId;
              const isPinnedTrailingActions = pinActionsColumn && colId === trailingActionsColumnId;

              return (
                <TableHead
                  key={colId}
                  style={{
                    width,
                    minWidth: col.minWidth,
                    ...(isPinnedIdentity ? stickyIdentityOffset(selectable) : {}),
                  }}
                  className={cn(
                    'relative',
                    col.align === 'center' && 'text-center',
                    col.align === 'right' && 'text-end',
                    isResizable && 'select-none',
                    isPinnedIdentity && [STICKY_LEAD_CELL_OFFSET, 'bg-background'],
                    isPinnedTrailingActions && [STICKY_ACTIONS_CELL, 'bg-background'],
                    col.cellClassName
                  )}
                >
                  {col.sortable && onSort ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ms-2 h-8 max-w-full px-2 data-[state=open]:bg-accent"
                      onClick={() => handleSort(colId)}
                    >
                      {/* No `dir`: a column label is our own translated copy, so
                          it takes the document direction (WI cf3fc9a4). */}
                      <span className="min-w-0 truncate">{col.headerLabel ?? col.label}</span>
                      {isSorted ? (
                        sortDirection === 'asc' ? (
                          <ArrowUp className="ms-1 h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <ArrowDown className="ms-1 h-3.5 w-3.5 shrink-0" />
                        )
                      ) : (
                        <ArrowUpDown className="ms-1 h-3.5 w-3.5 shrink-0" />
                      )}
                    </Button>
                  ) : (
                    <span>{col.headerLabel ?? col.label}</span>
                  )}
                  {isResizable && (
                    <div
                      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary"
                      onMouseDown={(e) =>
                        handleResizeStart(e, colId, typeof width === 'number' ? width : 150)
                      }
                    />
                  )}
                </TableHead>
              );
            })}
            {actionsColumn && (
              <TableHead
                style={{ width: actionsColumnWidth }}
                className={cn(
                  'text-end',
                  pinActionsColumn && [STICKY_ACTIONS_CELL, 'bg-background']
                )}
              >
                {t('app.actions')}
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && renderLoadingSkeleton()}
          {!isLoading && data.length === 0 && renderEmptyState()}
          {!isLoading &&
            data.map((row) => {
              const rowId = row[keyField] as string | number;
              const isRowSelected = selectedIds.includes(rowId);
              const rowSelectable = isRowSelectable ? isRowSelectable(row) : true;
              const rowLinkHref = rowHref ? rowHref(row) : null;
              const cells = (
                <>
                  {selectable && (
                    <TableCell
                      className={cn(
                        'w-[40px]',
                        pinLeadColumns && [STICKY_LEAD_CELL, STICKY_CELL_SURFACE]
                      )}
                    >
                      <div
                        className={cn(
                          'flex items-center justify-center',
                          rowSelectable ? 'cursor-pointer' : 'cursor-not-allowed'
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectRow(rowId, rowSelectable);
                        }}
                      >
                        <Checkbox
                          checked={isRowSelected}
                          disabled={!rowSelectable}
                          aria-label={t('app.selectRow')}
                        />
                      </div>
                    </TableCell>
                  )}
                  {visibleColumns.map((col) => {
                    const content = col.render ? col.render(row) : String(row[col.id as keyof T] ?? '');
                    // Wrap the identity cell as a real anchor when the row is a link,
                    // so cmd/middle-click works. Match by column id (`primaryLinkColumnId`),
                    // not position: if the identity column is hidden via ColumnSelector,
                    // no visible column matches, so nothing is wrapped (avoiding an <a>
                    // around a shifted-in, possibly-interactive column) — the whole-row
                    // click still navigates.
                    const isPrimaryLink = rowLinkHref && String(col.id) === primaryLinkColumnId;
                    const isPinnedIdentity = pinLeadColumns && String(col.id) === identityColumnId;
                    const isPinnedTrailingActions =
                      pinActionsColumn && String(col.id) === trailingActionsColumnId;
                    return (
                      <TableCell
                        key={String(col.id)}
                        style={isPinnedIdentity ? stickyIdentityOffset(selectable) : undefined}
                        className={cn(
                          col.align === 'center' && 'text-center',
                          col.align === 'right' && 'text-end',
                          isPinnedIdentity && [STICKY_LEAD_CELL_OFFSET, STICKY_CELL_SURFACE],
                          isPinnedTrailingActions && [STICKY_ACTIONS_CELL, STICKY_CELL_SURFACE],
                          col.cellClassName
                        )}
                      >
                        {isPrimaryLink ? (
                          <PrimaryCellLink href={rowLinkHref}>{content}</PrimaryCellLink>
                        ) : (
                          content
                        )}
                      </TableCell>
                    );
                  })}
                  {actionsColumn && (
                    <TableCell
                      className={cn(
                        'text-end',
                        pinActionsColumn && [STICKY_ACTIONS_CELL, STICKY_CELL_SURFACE]
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {actionsColumn(row)}
                    </TableCell>
                  )}
                </>
              );

              // The expansion row, when this row is open. `colSpan` counts every
              // header cell the table actually rendered — the drag-handle/prepend
              // column, the selection checkbox, the VISIBLE columns (auto-hide and
              // the column selector both shrink that set) and the actions column —
              // so a detail panel spans the full width at every breakpoint instead
              // of leaving a ragged tail of empty cells.
              const detail = renderRowDetail ? renderRowDetail(row) : null;
              const detailRow = detail ? (
                <TableRow className="hover:bg-transparent" data-testid="datatable-row-detail">
                  <TableCell
                    colSpan={
                      (prependColumnHeader ? 1 : 0) +
                      (selectable ? 1 : 0) +
                      visibleColumns.length +
                      (actionsColumn ? 1 : 0)
                    }
                    className="bg-muted/30 p-0"
                  >
                    {/* Pinned to the scroll port's inline start — see
                        `scrollportWidth`. `start-0` and not `left-0`, so the pin
                        mirrors under `dir="rtl"` with the table it sits in. */}
                    <div
                      className="sticky start-0 px-4 py-3"
                      style={scrollportWidth ? { width: scrollportWidth } : undefined}
                    >
                      {detail}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null;

              // Whether a row is WRAPPED keys on the PROP, never on whether this
              // particular row is open — and that distinction is the whole of it.
              // React reconciles by (key, element type), so a row that renders a
              // bare `<TableRow>` when closed and a `<Fragment>` when open changes
              // type at the same key and is unmounted and REMOUNTED on every
              // toggle: the disclosure button a keyboard user just pressed is
              // destroyed under them (focus falls back to `<body>`), and any
              // uncontrolled state elsewhere in that row — an open picker popover —
              // goes with it. Keyed on the prop, the type is stable for the table's
              // whole lifetime, and a table that passes no `renderRowDetail` renders
              // exactly what it did before.
              const wrapsDetail = !!renderRowDetail;

              if (renderRowWrapper) {
                const wrapped = renderRowWrapper(row, cells, String(rowId));
                if (!wrapsDetail) return wrapped;
                return (
                  <Fragment key={String(rowId)}>
                    {wrapped}
                    {detailRow}
                  </Fragment>
                );
              }

              // `datatable-row` marks a real, clickable data row — distinct from
              // the loading-skeleton rows and the totals row, neither of which
              // carry it. Tests/automation target this to avoid clicking a
              // skeleton placeholder while the query is still loading. Tables
              // that supply `renderRowWrapper` own their row markup (above) and
              // must add their own testid if they need one.
              if (rowHref) {
                const navigable = (
                  <NavigableRow
                    key={wrapsDetail ? undefined : String(rowId)}
                    href={rowLinkHref}
                    isSelected={isRowSelected}
                  >
                    {cells}
                  </NavigableRow>
                );
                if (!wrapsDetail) return navigable;
                return (
                  <Fragment key={String(rowId)}>
                    {navigable}
                    {detailRow}
                  </Fragment>
                );
              }

              const dataRow = (
                <TableRow
                  key={wrapsDetail ? undefined : String(rowId)}
                  data-testid="datatable-row"
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // `group/row` — see NavigableRow.
                  className={cn(
                    'group/row',
                    onRowClick && 'cursor-pointer',
                    isRowSelected && 'bg-muted/50',
                    rowClassName?.(row)
                  )}
                  data-state={isRowSelected ? 'selected' : undefined}
                >
                  {cells}
                </TableRow>
              );

              if (!wrapsDetail) return dataRow;
              return (
                <Fragment key={String(rowId)}>
                  {dataRow}
                  {detailRow}
                </Fragment>
              );
            })}
          {showTotals && !isLoading && data.length > 0 && visibleColumns.some((col) => col.total) && (
            <TableRow className="bg-muted/50 font-medium border-t-2">
              {selectable && (
                <TableCell className={cn(pinLeadColumns && [STICKY_LEAD_CELL, 'bg-muted/50'])} />
              )}
              {visibleColumns.map((col, index) => (
                <TableCell
                  key={String(col.id)}
                  style={
                    pinLeadColumns && String(col.id) === identityColumnId
                      ? stickyIdentityOffset(selectable)
                      : undefined
                  }
                  className={cn(
                    col.align === 'center' && 'text-center',
                    col.align === 'right' && 'text-end',
                    pinLeadColumns &&
                      String(col.id) === identityColumnId && [STICKY_LEAD_CELL_OFFSET, 'bg-muted/50'],
                    pinActionsColumn &&
                      String(col.id) === trailingActionsColumnId && [STICKY_ACTIONS_CELL, 'bg-muted/50'],
                    col.cellClassName
                  )}
                >
                  {index === 0 && !col.total
                    ? (totalsLabel || t('app.total'))
                    : col.total
                      ? col.total(data)
                      : ''}
                </TableCell>
              ))}
              {actionsColumn && (
                <TableCell className={cn(pinActionsColumn && [STICKY_ACTIONS_CELL, 'bg-muted/50'])} />
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
      </div>
    </div>
  );

  const renderPagination = () => {
    if (!showPagination && !onPageSizeChange) return null;

    return (
      <div className="flex items-center justify-between mt-4 gap-2 flex-wrap">
        {onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {t('pagination.rowsPerPage', 'Rows per page:')}
            </span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div />
        )}
        {showPagination && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              {t('app.previous')}
            </Button>
            <span className="flex items-center px-2 text-sm text-muted-foreground">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              {t('app.next')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      {showCardView ? renderMobileCardView() : renderTableView()}
      {renderPagination()}
    </div>
  );
}
